/**
 * Shared field validation for every public lead form on the site.
 * Used by the client forms (inline errors) and by /api/site-submit (authoritative),
 * so both stay in sync. Each validator returns an error string, or null if valid.
 */

export const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
// US ZIP: 5 digits, optionally followed by +4 (e.g. 92127 or 92127-1234)
export const ZIP_RE = /^\d{5}(-\d{4})?$/

export function validateName(value: string): string | null {
  const v = value.trim()
  if (!v) return 'Name is required.'
  if (v.length < 2) return 'Please enter your full name.'
  return null
}

export function validateEmail(value: string): string | null {
  const v = value.trim()
  if (!v) return 'Email is required.'
  if (v.length > 200 || !EMAIL_RE.test(v)) return 'Please enter a valid email address.'
  return null
}

export function validatePhone(value: string): string | null {
  const v = value.trim()
  if (!v) return 'Phone is required.'
  // Allow common separators and an optional country code; count only digits.
  if (/[^\d\s()+\-.]/.test(v)) return 'Please enter a valid phone number.'
  const digits = v.replace(/\D/g, '')
  if (digits.length < 10 || digits.length > 15) return 'Please enter a valid phone number.'
  return null
}

export function validateZip(value: string): string | null {
  const v = value.trim()
  if (!v) return 'Zip code is required.'
  if (!ZIP_RE.test(v)) return 'Please enter a valid ZIP code (e.g. 92127).'
  return null
}

export function validateService(value: string): string | null {
  if (!value.trim()) return 'Please select a project type.'
  return null
}

export function validateMessage(value: string): string | null {
  if (!value.trim()) return 'Message is required.'
  return null
}

export type ContactFields = {
  fullName?: string
  email?: string
  phone?: string
  zip?: string
  service?: string
  message?: string
}

export type ContactValidationOptions = {
  /** Use 'name' instead of 'fullName' as the error key (the ads consultation form). */
  nameKey?: 'fullName' | 'name'
  requireZip?: boolean
  requireService?: boolean
  requireMessage?: boolean
}

/**
 * Build an error map for a contact-style submission. `name`, `email` and `phone`
 * are always validated; `zip`/`service`/`message` only when their `require*` flag is set.
 */
export function validateContactFields(
  fields: ContactFields,
  opts: ContactValidationOptions = {},
): Record<string, string> {
  const errs: Record<string, string> = {}
  const nameKey = opts.nameKey ?? 'fullName'

  const nameErr = validateName(fields.fullName ?? '')
  if (nameErr) errs[nameKey] = nameErr

  const emailErr = validateEmail(fields.email ?? '')
  if (emailErr) errs.email = emailErr

  const phoneErr = validatePhone(fields.phone ?? '')
  if (phoneErr) errs.phone = phoneErr

  if (opts.requireZip) {
    const zipErr = validateZip(fields.zip ?? '')
    if (zipErr) errs.zip = zipErr
  }
  if (opts.requireService) {
    const serviceErr = validateService(fields.service ?? '')
    if (serviceErr) errs.service = serviceErr
  }
  if (opts.requireMessage) {
    const messageErr = validateMessage(fields.message ?? '')
    if (messageErr) errs.message = messageErr
  }

  return errs
}
